import React, { useState, useRef, useEffect } from "react";
import "./CheckpointIndex.css";
import { Button, ActionMenu, GenericTable, tCell, toastMessage, Dialog, Checkbox } from "../utils";
import theme, { buttonClasses } from "../utils/theme";
import Genericfilter from "../utils/Genericfilter";
import { useCheckpoints, useOrgs, usePlant, useUserDetail, useUserPermissions } from "../../services/hooks";
import { debounce, capitalizeCamelCase, isObjectEqual } from "../../services/functions";
import { GenericstatsV2 } from "../utils/Genericstats";
import { Box, Typography } from "@mui/material";
import { useUpdateUserPermissionMutation } from "../../api/userSlice";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { usePlantContext } from "../../context/plantContext";

const defaultFilter = {
	pagesize: 10,
	page_no: 1,
};

export default function CheckpointIndex() {
	const location = useLocation();
	const dataReceived = location.state?.data;
	const isModalReceived = location.state?.assignUserModal;
	const filterData = location.state?.filterData;
	const navigate = useNavigate();
	const { isSuperAdmin } = useUserDetail();
	const { userPermissions, isFetched } = useUserPermissions();
	const { plantId } = usePlantContext();
	const shouldHandlePlant = isSuperAdmin || userPermissions?.managePlant?.value;
	// console.log("filterData", filterData)
	const filterRef = useRef(defaultFilter);
	if (filterData) filterRef.current = { ...filterRef.current, ...filterData };
	if (!isSuperAdmin) {
		filterRef.current.plantIds = plantId;
	}
	const { orgOptions } = useOrgs({}, { skip: !isSuperAdmin });
	const { plantOptions, getPlantDetails, clearPlants } = usePlant();
	useEffect(() => {
		if (userPermissions?.managePlant?.value) {
			getPlantDetails({});
		}
	}, [userPermissions]);
	let filterFields = [
		{
			inputType: "textfield",
			id: "1",
			name: "displayName",
			type: "text",
			label: "Display Name",
			placeholder: "Display Name",
			readOnly: false,
			error: "",
			value: filterRef.current?.displayName,
		},
	];
	if (isSuperAdmin) {
		filterFields.push({
			inputType: "dropdown",
			id: "orgId",
			name: "orgId",
			label: "Organization",
			option: orgOptions,
			value: filterRef.current?.orgId,
		});
	}
	if (isSuperAdmin) {
		filterFields.push({
			inputType: "dropdown",
			id: "plantIds",
			name: "plantIds",
			label: "Warehouse",
			error: "",
			option: plantOptions,
			value: filterRef.current?.plantIds,
		});
	}

	const [fetchStats, setFetchStats] = useState(false);

	const [isModal, setIsModal] = useState(false);
	const [data, setData] = useState({});

	const { page_no = 1, pagesize = 10 } = filterRef.current;

	const { checkpoints, count, isLoading, getCheckpoints } = useCheckpoints(filterRef.current);
	useEffect(() => {
		if (dataReceived) {
			setData(dataReceived);
		}
		if (isModalReceived) {
			setIsModal(isModalReceived);
			setFetchStats((old) => !old);
		}
	}, [dataReceived, isModalReceived]);
	const handleDeviceView = (e, index) => {
		const { latitude, longitude } = checkpoints[index];
		if (latitude != null && longitude != null && latitude !== undefined && longitude !== undefined) {
			// Redirect to Google Maps with the specified latitude and longitude
			window.open(`https://www.google.com/maps?q=${latitude},${longitude}`, "_blank");
		} else {
			toastMessage(false, "Location is not assigned to this checkpoint.");
		}
	};
	// Using this useEffect to resolve the issue of page refresh state.
	// The problem was: when we refreshed the page and currentFilters existed, the filters were not being cleared.
	useEffect(() => {
		if (location.state) navigate(".", { replace: true });
	}, [navigate]);

	useEffect(() => {
		if (filterData?.orgId && isSuperAdmin) {
			getPlantDetails({ orgId: filterData.orgId });
		}
	}, [filterData]);
	let actionHandlers = {};
	if (userPermissions?.updateCheckpoint?.value || isSuperAdmin) {
		actionHandlers.edit = (id) => {
			navigate("/checkpoints/edit-checkpoint", { state: { updatedData: checkpoints[id], filterData: filterRef.current } });
		};
	}
	if (userPermissions?.addCheckpoint?.value || isSuperAdmin) {
		actionHandlers.duplicate = (id) => {
			navigate("/checkpoints/edit-checkpoint", { state: { updatedData: checkpoints[id], isDuplicate: true, filterData: filterRef.current } });
		};
	}

	let header = ["S.no", "Checkpoint Number", "Display Name", "Checkpoint Type", "Send Approval", "Dependent Approval"];
	if (isSuperAdmin) header.push("Organization");
	if (isSuperAdmin) header.push("Warehouse");
	if (userPermissions?.addCheckpoint?.value || userPermissions?.updateCheckpoint?.value || isSuperAdmin) {
		header.push("Action");
	}

	const rows =
		checkpoints?.map((_, i) => {
			let row = [];
			row.push(
				tCell((page_no - 1) * pagesize + i + 1),
				tCell(_.checkpointNumber),
				tCell(_.displayName, theme.themeOrange, "pointer", handleDeviceView, i),
				// tCell(_.orgName),
				tCell(_.checkpointType),
				tCell(_.sendApproval ? "Yes" : "No"),
				tCell(_.dependentApproval ? "Yes" : "No")
			);
			if (isSuperAdmin) row.push(tCell(_.orgName));
			if (isSuperAdmin) row.push(tCell(_?.plantName || "-"));
			if (userPermissions?.addCheckpoint?.value || userPermissions?.updateCheckpoint?.value || isSuperAdmin) {
				row.push(<ActionMenu id={i} handlers={actionHandlers} />);
			}
			return row;
		}) || [];

	const onPageChange = (page_no) => {
		filterRef.current = { ...filterRef.current, page_no: page_no + 1 };
		getCheckpoints(filterRef.current);
	};
	const onPageSizeChange = (e) => {
		const { value } = e.target;
		filterRef.current = { ...filterRef.current, pagesize: value, page_no: 1 };
		getCheckpoints(filterRef.current);
	};

	/**
	 * We use GenericFilter component for all CRUD filters. That component maintains its own state for the filter
	 * and that object is sent here to the handleFilterChange function.
	 * Mutating this _filter object will cause the filter state to change on rerender (It's probably because of object reference in JS. The filter object from the state gets mutated and then the component gets re-rendered  due to any reason and now the state will have the mutated object)
	 * Here we have relationship between Org and Plant. On selecting an org, we will fetch its plant. So in order to clear previous selected plant when selecting different org, we will mutate the _filter object
	 */
	const handleFilterChange = (_filter) => {
		let filter = { ..._filter };

		for (let key in filter) {
			if (!filter[key]) delete filter[key];
		}
		if (isSuperAdmin) {
			if (filter.orgId) {
				// filter.orgId = filter.orgId?.value;
				if (shouldHandlePlant && filter.orgId != filterRef.current?.orgId) {
					// when switching organization, we want to
					// - get plants for that org
					// - clear plantIds from the local filter object
					// - clear plantIds from original filter object, mutating the state and clearing the plant dropdown
					delete filter.plantIds;
					delete _filter.plantIds;
					getPlantDetails({ orgId: filter.orgId });
				}
			} else {
				delete filter.plantIds;
				clearPlants();
			}
		}
		// if (filter.plantIds) filter.plantIds = filter.plantIds?.value;

		filter.page_no = 1;
		filter.pagesize = 10;
		if (plantId) {
			filter.plantIds = plantId;
		}
		if (!isObjectEqual(filterRef.current, filter)) {
			filterRef.current = { ...filter };
			getCheckpoints(filter);
		}
		// getCheckpoints(filter);
	};
	const handleAddCheckpoint = () => {
		navigate("/checkpoints/add-checkpoint", { state: { filterData: filterRef.current } });
	};

	if (isFetched && userPermissions && Object.keys(userPermissions).length && !userPermissions.getCheckpoint?.value && !isSuperAdmin) {
		return <Navigate to="/not-authorized" replace />;
	}
	return (
		<>
			<div className="checkpoint_panel">
				<div className="checkpoint_heading">Checkpoint</div>
				{isSuperAdmin || userPermissions?.addCheckpoint?.value ? (
					<div>
						<div className="cust-col-5 flex-jc-end">
							{/* <Link className="a_tag_no" to={"/checkpoints/add-checkpoint"}> */}
							<div>
								<Button text="Add Checkpoint" style={buttonClasses.lynkitOrangeEmpty} onClick={handleAddCheckpoint} />
							</div>
							{/* </Link> */}
						</div>
					</div>
				) : null}
			</div>
			<>
				<GenericstatsV2 statsFor={"checkpoint"} fetchStats={fetchStats} plantId={plantId} />
				<div className="cust-row flex-algn-cent">
					<Genericfilter key={plantId} filterFields={filterFields} onFilterChange={debounce((filter) => handleFilterChange(filter), 500)} addOnClear />
				</div>
				<div>
					<GenericTable
						header={header}
						rows={rows}
						pageCount={count}
						pageNo={page_no}
						limit={pagesize}
						onPageChange={onPageChange}
						onPageSizeChange={onPageSizeChange}
						isLoading={isLoading}
					/>
				</div>
			</>
			{isModal ? <AssignToUsers onClose={() => setIsModal(false)} open={isModal} users={data.users} checkpoint={data.createdCheckpoint} /> : null}
		</>
	);
}

function AssignToUsers({ onClose, open, users, checkpoint }) {
	if (!users || !users.length || !checkpoint) {
		console.log("No users / Nothing to quick assign");
		return;
	}
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [permissions, setPermissions] = useState({});
	const [updateUserPermissionTrigger] = useUpdateUserPermissionMutation();

	const handleFormSubmit = async () => {
		const payload = { userId: [...selectedUsers], permissions: { ...permissions }, module: "vms", project: "lynkid", mergeWithExisting: true };
		// console.log(" THIS IS THE PAYLOAD --> ", payload);

		updateUserPermissionTrigger(payload)
			.unwrap()
			.then((res) => {
				if (!res.error) {
					toastMessage(true, res.message);
					onClose();
					setSelectedUsers([]);
					setPermissions({});
				} else {
					toastMessage(false, res.message);
				}
			})
			.catch();
	};

	const handleCheckboxChange = (id, checked) => {
		let selected = selectedUsers.filter((users) => users != id);
		if (checked) {
			selected.push(id);
		}
		setSelectedUsers(selected);
	};

	const handleClose = () => {
		onClose();
	};

	const handleCheckpointPermChange = (e, id) => {
		const { name, checked } = e.target;
		// console.log(name,checked,id)
		setPermissions((old) => {
			let update = JSON.parse(JSON.stringify(old));
			if (checked) {
				update[name] = checkpointsPerms[checkpoint._id][name];
			} else {
				delete update[name];
			}
			return update;
		});
	};

	let checkpointsPerms = makeCheckpointPerms([checkpoint]);
	let id = checkpoint._id;
	let perms = [];
	for (let key in checkpointsPerms[id]) {
		if (key != "key" && key != "permObj" && key.split("-").length == 3) {
			perms.push(
				<Checkbox
					key={key}
					label={capitalizeCamelCase(key.split("-")[2])}
					id={key}
					name={key}
					checked={permissions[key]}
					onChange={(e) => {
						handleCheckpointPermChange(e, id);
					}}
				/>
			);
		}
	}
	useEffect(() => {
		let newPerms = checkpointsPerms[id];
		delete newPerms["key"];
		delete newPerms["permObj"];
		setPermissions(newPerms);
	}, []);

	return (
		<Dialog size="xs" open={open} handleClose={handleClose} handleSubmit={handleFormSubmit} title={`Assign ${checkpoint.displayName} to users`} isSubmitDisabled={false}>
			<Box component="form">
				<Box component="fieldset" sx={{ border: "1px solid var(--input-border-color)", margin: "0", padding: "revert", display: "grid", gridTemplateColumns: "1fr" }}>
					<Typography component="legend" sx={{ fontSize: "1rem", p: "0 0.25rem", float: "none", width: "revert" }}>
						Permissions
					</Typography>
					{perms}
				</Box>

				<Box component="fieldset" sx={{ border: "1px solid var(--input-border-color)", margin: "1rem 0 0 0", padding: "revert", display: "grid", gridTemplateColumns: "1fr" }}>
					<Typography component="legend" sx={{ fontSize: "1rem", p: "0 0.25rem", float: "none", width: "revert" }}>
						Users
					</Typography>
					{users.map((user, i) => {
						return (
							<Checkbox
								id={i}
								name={user._id}
								label={
									<span>
										{user.name}
										{<span className="mini-label"> ({user.email})</span>}
									</span>
								}
								checked={selectedUsers.includes(user._id)}
								onChange={(e) => handleCheckboxChange(e.target.name, e.target.checked)}
							/>
						);
					})}
				</Box>
			</Box>
		</Dialog>
	);
}

const permObj = { dependancy: [], strict: false, value: true };
function makeCheckpointPerms(checkpoints = []) {
	return checkpoints.reduce((res, curr) => {
		const key = `checkpoint-${curr._id}`;
		const listConfigPerms = curr.listingConfig?.reduce((res, curr) => {
			res[`${key}-${curr.type}`] = permObj;
			return res;
		}, {});
		let additionalPerms = {};
		if (curr.assetConfig && Object.keys(curr.assetConfig).length) {
			for (let permType in curr.assetConfig) {
				//check if the particular permission is enabled at the current checkpoint
				if (curr.assetConfig[permType]) {
					additionalPerms[`${key}-${permType}`] = { ...permObj, dependancy: [permType], strict: true };
				}
			}
		}
		res[curr._id] = {
			[key]: permObj,
			key,
			permObj,
			[`${key}-manualEntry`]: permObj,
			[`${key}-viewSubmit`]: permObj,
			[`${key}-viewDetail`]: permObj,
			[`${key}-viewListing`]: permObj,
			...(listConfigPerms || {}),
			...additionalPerms,
		};
		// console.log(additionalPerms)
		return res;
	}, {});
}
